<template>
  <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
    <label v-if="requestData?.type === 'supplies'" class="flex min-w-0 flex-col gap-1 text-sm text-black">
      {{ t('requestDetailAdmin.internalOperation.registrationClassification') }}
      <select
        v-model="details.registration_classification"
        :disabled="disabled || isSaving"
        @change="isDirty = true"
        class="w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-[#7CADB9] disabled:cursor-not-allowed disabled:bg-gray-100"
      >
        <option value="">{{ t('common.select') }}</option>
        <option value="urgent">{{ t('requestDetailAdmin.internalOperation.classifications.urgent') }}</option>
        <option value="nnss">{{ t('requestDetailAdmin.internalOperation.classifications.nnss') }}</option>
      </select>
    </label>

    <!-- Gestor responsable de la cartera del cliente: solo lectura, sale de la
         cuenta cliente (client_account.commercial_label) igual que en el export -->
    <div class="flex min-w-0 flex-col gap-1 text-sm text-black">
      {{ t('requestDetailAdmin.internalOperation.portfolioManager') }}
      <span class="w-full min-w-0 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-[#7CADB9]">
        {{ requestData?.client_account?.commercial_label || '-' }}
      </span>
    </div>

    <label class="flex min-w-0 flex-col gap-1 text-sm text-black">
      {{ t('requestDetailAdmin.internalOperation.pendingReason') }}
      <select
        v-model="details.pending_reason"
        :disabled="disabled || isSaving"
        @change="isDirty = true"
        class="w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-[#7CADB9] disabled:cursor-not-allowed disabled:bg-gray-100"
      >
        <option value="">{{ t('common.select') }}</option>
        <option value="marketer_response">{{ t('requestDetailAdmin.internalOperation.pendingReasons.marketerResponse') }}</option>
        <option value="distributor_response">{{ t('requestDetailAdmin.internalOperation.pendingReasons.distributorResponse') }}</option>
        <option value="client_response">{{ t('requestDetailAdmin.internalOperation.pendingReasons.clientResponse') }}</option>
        <option value="client_documentation">{{ t('requestDetailAdmin.internalOperation.pendingReasons.clientDocumentation') }}</option>
      </select>
    </label>
  </div>

  <div class="flex justify-end pt-2">
    <ButtonWhite
      :disabled="disabled || isSaving"
      class="flex items-center gap-2"
      :title="isSaving ? t('requestDetailAdmin.internalOperation.savingOperational') : t('requestDetailAdmin.internalOperation.saveOperational')"
      @click="save"
    />
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue3-toastify';
import ButtonWhite from '@/components/button/ButtonWhite.vue';

const props = defineProps({
  requestData: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved']);
const { t } = useI18n();
const details = reactive({
  registration_classification: '',
  pending_reason: '',
});
const isSaving = ref(false);
const isDirty = ref(false);
let synchronizedRequestId = null;

watch(
  () => [
    props.requestData?.id,
    props.requestData?.type,
    props.requestData?.registration_classification,
    props.requestData?.pending_reason,
  ],
  ([requestId, , registrationClassification, pendingReason]) => {
    if (requestId !== synchronizedRequestId) {
      synchronizedRequestId = requestId;
      isDirty.value = false;
    }
    if (isDirty.value) return;

    details.registration_classification = registrationClassification ?? '';
    details.pending_reason = pendingReason ?? '';
  },
  { immediate: true }
);

const save = async () => {
  if (!props.requestData?.id || props.disabled || isSaving.value) return;

  isSaving.value = true;
  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/${props.requestData.id}/operational`,
      {
        registration_classification: props.requestData.type === 'supplies'
          ? details.registration_classification || null
          : null,
        pending_reason: details.pending_reason || null,
      },
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (!response.data?.value) {
      toast.error(t('requestDetailAdmin.internalOperation.operationalSaveError'));
      return;
    }

    details.registration_classification = response.data.value.registration_classification ?? '';
    details.pending_reason = response.data.value.pending_reason ?? '';
    isDirty.value = false;
    emit('saved', { ...response.data.value });
    toast.success(t('requestDetailAdmin.internalOperation.operationalSaved'));
  } catch (error) {
    console.error('Error updating operational details:', error);
    toast.error(t('requestDetailAdmin.internalOperation.operationalSaveError'));
  } finally {
    isSaving.value = false;
  }
};
</script>
